import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, shade } from './draw-utils';
import { blotches, glow, makeRock, makeTile, makeWallBlob, wrap } from './env-kit';
import type { ProceduralDef } from '../types';

/**
 * Theme kits S1 Scrap Belt + S2 Elemental Core (docs/04 environment themes):
 * floor, wall mass, edge walls, cover rocks and the landmark. Floors stay
 * dark and desaturated so warm enemy shots and cyan player shots pop.
 */
const SCRAP = { floor: 0x2f3a40, seam: 0x222b30, plate: 0x36434a, rivet: 0x56656c, rust: 0x6a4630 };
const CORE = { floor: 0x2a1d22, crack: 0x170f12, vein: 'rgba(255, 110, 40, 0.13)', ash: 0x34252a };

// ---- S1 Scrap Belt ----

export const envGroundScrap = makeTile('env/ground-scrap', SCRAP.floor, 11, (ctx, w, h, rnd) => {
  blotches(ctx, w, h, rnd, SCRAP.rust, 0.06, 7, 26, 70);
  // 2×2 big deck plates: soft inner panel, one sub-seam each, corner rivets
  const P = w / 2;
  for (let px = 0; px < 2; px++) {
    for (let py = 0; py < 2; py++) {
      const x0 = px * P;
      const y0 = py * P;
      ctx.fillStyle = css(SCRAP.plate);
      ctx.globalAlpha = 0.35;
      ctx.fillRect(x0 + 10, y0 + 10, P - 20, P - 20);
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = css(SCRAP.seam);
      ctx.lineWidth = 2;
      ctx.beginPath();
      if (rnd() < 0.5) {
        const sx = x0 + P * (0.3 + rnd() * 0.4);
        ctx.moveTo(sx, y0 + 10); ctx.lineTo(sx, y0 + P - 10);
      } else {
        const sy = y0 + P * (0.3 + rnd() * 0.4);
        ctx.moveTo(x0 + 10, sy); ctx.lineTo(x0 + P - 10, sy);
      }
      ctx.stroke();
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = css(SCRAP.rivet);
      for (const [rx, ry] of [[14, 14], [P - 14, 14], [14, P - 14], [P - 14, P - 14]] as const) {
        circle(ctx, x0 + rx, y0 + ry, 2.6);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
  }
  ctx.strokeStyle = 'rgba(20, 26, 30, 0.7)';
  ctx.lineWidth = 3;
  for (const v of [0, P, w]) {
    ctx.beginPath(); ctx.moveTo(v, 0); ctx.lineTo(v, h); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, v); ctx.lineTo(w, v); ctx.stroke();
  }
  ctx.strokeStyle = 'rgba(160, 180, 188, 0.09)';
  ctx.lineWidth = 1.5;
  for (let i = 0; i < 14; i++) {
    const x = rnd() * w;
    const y = rnd() * h;
    const a = rnd() * Math.PI;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(a) * 28, y + Math.sin(a) * 28);
    ctx.stroke();
  }
}, 512);

export const envWallMassScrap = makeTile('env/wallmass-scrap', 0x151b1f, 12, (ctx, w, h, rnd) => {
  blotches(ctx, w, h, rnd, 0x1e262b, 0.9, 9, 20, 44);
  ctx.strokeStyle = 'rgba(70, 86, 92, 0.35)';
  ctx.lineWidth = 2;
  for (let i = -2; i < 6; i++) {
    ctx.beginPath();
    ctx.moveTo(i * 64, 0);
    ctx.lineTo(i * 64 + h, h);
    ctx.stroke();
  }
});

export const envWallScrap = makeWallBlob('env/wall-scrap', 0x4b5a61, 0x65767d, SCRAP.rust);
export const envRockScrap = makeRock('env/rock-scrap', 'chunk', 0x5d6c73, 0x7a8b92, 0x2d373c);

/** Derelict reactor: octagonal hull, vent ring, cracked glowing core. */
export const envLandmarkScrap: ProceduralDef = {
  key: 'env/landmark-scrap', width: 384, height: 384, anchorX: 0.5, anchorY: 0.5, frames: 1,
  draw(ctx, w) {
    const c = w / 2;
    const R = c - 14;
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI / 4) * i + Math.PI / 8;
      ctx.lineTo(c + Math.cos(a) * R, c + Math.sin(a) * R);
    }
    ctx.closePath();
    fillOutlined(ctx, 0x4f5e65, 9);
    circle(ctx, c, c, R * 0.74);
    fillOutlined(ctx, 0x3b474d, 6);
    // Hazard ring
    ctx.lineWidth = 14;
    for (let i = 0; i < 16; i++) {
      ctx.strokeStyle = i % 2 === 0 ? css(COLORS.playerYellow) : '#1d1430';
      ctx.beginPath();
      ctx.arc(c, c, R * 0.62, (Math.PI * 2 * i) / 16, (Math.PI * 2 * (i + 1)) / 16);
      ctx.stroke();
    }
    // Vents
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i;
      ctx.save();
      ctx.translate(c + Math.cos(a) * R * 0.86, c + Math.sin(a) * R * 0.86);
      ctx.rotate(a);
      ctx.beginPath();
      ctx.roundRect(-10, -22, 20, 44, 6);
      fillOutlined(ctx, 0x2a3337, 4);
      ctx.restore();
    }
    circle(ctx, c, c, R * 0.4);
    fillOutlined(ctx, 0x26302f, 6);
    glow(ctx, c, c, R * 0.36, '255, 170, 70', 0.85);
    ctx.strokeStyle = css(0x1d1430);
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(c - R * 0.3, c - R * 0.12); ctx.lineTo(c - R * 0.05, c + R * 0.04);
    ctx.lineTo(c + R * 0.12, c - R * 0.2); ctx.moveTo(c - R * 0.05, c + R * 0.04);
    ctx.lineTo(c + R * 0.08, c + R * 0.3);
    ctx.stroke();
    // Highlight
    ctx.globalAlpha = 0.18;
    circle(ctx, c - R * 0.35, c - R * 0.4, R * 0.35);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.globalAlpha = 1;
  },
};

// ---- S2 Elemental Core ----

export const envGroundCore = makeTile('env/ground-core', CORE.floor, 21, (ctx, w, h, rnd) => {
  blotches(ctx, w, h, rnd, CORE.ash, 0.3, 9, 30, 80);
  // Cracked basalt: a few thin dark cracks over a DIM ember glow (shots stay readable)
  for (let i = 0; i < 6; i++) {
    let x = rnd() * w;
    let y = rnd() * h;
    const pts: Array<[number, number]> = [[x, y]];
    for (let k = 0; k < 4; k++) {
      x += (rnd() - 0.5) * 120;
      y += (rnd() - 0.5) * 120;
      pts.push([x, y]);
    }
    wrap(w, h, (dx, dy) => {
      ctx.beginPath();
      pts.forEach(([px, py], j) => (j === 0 ? ctx.moveTo(px + dx, py + dy) : ctx.lineTo(px + dx, py + dy)));
      ctx.strokeStyle = CORE.vein;
      ctx.lineWidth = 6;
      ctx.stroke();
      ctx.strokeStyle = css(CORE.crack);
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  }
  ctx.fillStyle = 'rgba(255, 150, 70, 0.22)';
  for (let i = 0; i < 8; i++) {
    circle(ctx, rnd() * w, rnd() * h, 1.4);
    ctx.fill();
  }
}, 512);

export const envWallMassCore = makeTile('env/wallmass-core', 0x140b0e, 22, (ctx, w, h, rnd) => {
  blotches(ctx, w, h, rnd, 0x21141a, 0.9, 10, 16, 40);
  ctx.fillStyle = 'rgba(255, 110, 40, 0.35)';
  for (let i = 0; i < 9; i++) {
    circle(ctx, rnd() * w, rnd() * h, 1.2 + rnd() * 1.6);
    ctx.fill();
  }
});

export const envWallCore = makeWallBlob('env/wall-core', 0x3d2a2e, 0x573c3f, COLORS.lavaOrange);
export const envRockCore = makeRock('env/rock-core', 'chunk', 0x4a3438, 0x664649, COLORS.terraCrack);

/** Crystal spire cluster: radiating crystals, molten heart, cyan accents. */
export const envLandmarkCore: ProceduralDef = {
  key: 'env/landmark-core', width: 384, height: 384, anchorX: 0.5, anchorY: 0.5, frames: 1,
  draw(ctx, w) {
    const c = w / 2;
    const R = c - 16;
    circle(ctx, c, c, R * 0.62);
    fillOutlined(ctx, 0x3a2629, 8);
    glow(ctx, c, c, R * 0.7, '255, 122, 48', 0.45);
    const spikes = 9;
    for (let i = 0; i < spikes; i++) {
      const a = (Math.PI * 2 * i) / spikes + 0.2;
      const len = R * (i % 3 === 0 ? 1 : i % 3 === 1 ? 0.82 : 0.68);
      const half = 0.17;
      const cyan = i % 4 === 1;
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a - half) * R * 0.3, c + Math.sin(a - half) * R * 0.3);
      ctx.lineTo(c + Math.cos(a) * len, c + Math.sin(a) * len);
      ctx.lineTo(c + Math.cos(a + half) * R * 0.3, c + Math.sin(a + half) * R * 0.3);
      ctx.closePath();
      fillOutlined(ctx, cyan ? 0xffd27a : 0xff8a3c, 6);
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a - half * 0.5) * R * 0.32, c + Math.sin(a - half * 0.5) * R * 0.32);
      ctx.lineTo(c + Math.cos(a) * len * 0.92, c + Math.sin(a) * len * 0.92);
      ctx.lineTo(c + Math.cos(a) * R * 0.34, c + Math.sin(a) * R * 0.34);
      ctx.closePath();
      ctx.fillStyle = css(shade(cyan ? 0xffd27a : 0xff8a3c, 1.35));
      ctx.fill();
    }
    circle(ctx, c, c, R * 0.28);
    fillOutlined(ctx, 0xffc46a, 6);
    glow(ctx, c, c, R * 0.24, '255, 245, 200', 0.9);
  },
};
